<template>
  <section data-page-type="no-header" data-test-component="createMember">
    <page-spinner :spinner-url-list="spinnerUrlList" />
    <div v-if="contentLoaded" data-page-type="no-header">
      <input id="pageID" type="hidden" value="proposal_flow" />
      <div id="personal_information" data-page-type="flow">
        <!-- Sitecore Placeholder "phRightDesktopTopMobile" -->
        <slot name="topmobile" />
        <div id="mainView" class="flow__body no-footer">
          <form id="redirectForm" method="post" :action="formActionRedirectLink">
            <div class="container">
              <div class="row">
                <div class="column small-12 large-6 push-down">
                  <input
                    id="transferData"
                    type="hidden"
                    name="transferData"
                    :value="redirectTransferData"
                  />

                  <router-view @component-mounted="dispatchEventMethod" @no-result-click="goNext" />
                </div>
                <div class="column small-12 large-6">
                  <sticky-basket :price="totalPrice">
                    <template #label-mobile>
                      <span v-html="totalPriceLabel" />
                    </template>
                    <template #label>
                      <span v-html="totalPriceLabel" />
                    </template>
                  </sticky-basket>

                  <div class="row">
                    <div class="small-12 medium-12 large-10 large-offset-2 columns">
                      <!-- Sitecore Placeholder "phRightDesktopTopMobile" -->
                      <slot name="rightcolumn" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="column small-12 medium-4 large-3">
                  <router-link
                    v-if="showPrevButton"
                    :class="['button text-center', prevButton.class]"
                    :to="prevButton.path!"
                    data-test-prev-button
                  >
                    <span v-html="prevButton.label" />
                  </router-link>
                </div>
                <div class="column small-12 medium-4 large-3">
                  <router-link
                    v-if="showNextButton"
                    :class="['button text-center', nextButton.class]"
                    :to="nextButton.path!"
                    data-test-next-button
                  >
                    <span v-html="nextButton.label" />
                  </router-link>
                  <a
                    v-if="noResultAfterAllQuestions && isStepTwo"
                    :href="noResultPhoneLinkUrl"
                    class="button primary text-center"
                    v-html="noResultPhoneLinkLabel"
                  />
                  <a
                    v-if="isStudent && isStepTwo"
                    :href="studentInsuranceLink.url"
                    class="button primary text-center"
                    target="_blank"
                    v-html="studentInsuranceLink.text"
                  />
                  <input
                    v-if="isStepThree && matchedBrandAffinityLink !== null"
                    type="submit"
                    data-test-create-member-step3-submit
                    class="button primary text-center"
                    :value="matchedBrandAffinityLink.text"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <logo-footer :datasource="logoFooterDatasource" />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import StickyBasket from '../../../common/FlowBasket.vue';
import PageSpinner from '../../UI/PageSpinner.vue';
import LogoFooter from '../LogoFooter/LogoFooter.vue';
import Utility from '../../../common/UtilityFunctions.js';
import { CreateMemberStoreState } from '../../../store/modules/interfaces/CreateMemberStoreState';
import { defineComponent } from 'vue';
import { mapState } from 'vuex';

export default defineComponent({
  name: 'CreateMember',
  components: {
    PageSpinner,
    StickyBasket,
    LogoFooter
  },
  props: {
    datasourceId: {
      type: String,
      default() {
        return null;
      }
    }
  },
  computed: {
    ...mapState('createMember', {
      nextButton: (state: any) => (state as CreateMemberStoreState).buttons.nextButton,
      prevButton: (state: any) => (state as CreateMemberStoreState).buttons.prevButton,
      totalPriceLabel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.sharedFields.totalPriceLabel,
      totalPrice: (state: any) =>
        (state as CreateMemberStoreState).vueModel.sharedFields.totalPrice,
      contentLoaded: (state: any) => (state as CreateMemberStoreState).contentLoaded,
      currentBrandMatch: (state: any) => (state as CreateMemberStoreState).currentBrandMatch,
      noResultAfterAllQuestions: (state: any) =>
        (state as CreateMemberStoreState).noResultAfterAllQuestions,
      noResultPhoneLinkLabel: (state: any) =>
        (state as CreateMemberStoreState).vueModel.stepTwoMemberQuestions.noResultPhoneLinkLabel,
      noResultPhoneLinkUrl: (state: any) =>
        (state as CreateMemberStoreState).vueModel.stepTwoMemberQuestions.noResultPhoneLinkUrl,
      isStudent: (state: any) => (state as CreateMemberStoreState).isStudent,
      studentInsuranceLink: (state: any) => (state as CreateMemberStoreState).studentInsuranceLink,
      hasBrandAffinityMatch: (state: any) =>
        (state as CreateMemberStoreState).hasBrandAffinityMatch,
      matchedBrandAffinityLink: (state: any) =>
        (state as CreateMemberStoreState).matchedBrandAffinityLink,
      redirectTransferData: (state: any) => (state as CreateMemberStoreState).redirectTransferData,
      productsInBasket: (state: any) => (state as CreateMemberStoreState).productsInBasket,
      stepOnePath: (state: any) => (state as CreateMemberStoreState).stepOnePath,
      stepTwoPath: (state: any) => (state as CreateMemberStoreState).stepTwoPath,
      stepThreePath: (state: any) => (state as CreateMemberStoreState).stepThreePath
    }),
    spinnerUrlList(): Array<String> {
      return ['/api/eligibility/get/' + this.datasourceId];
    },
    showPrevButton(): Boolean {
      return this.prevButton.path !== null;
    },
    showNextButton(): Boolean {
      return this.nextButton.path !== null;
    },
    logoFooterDatasource(): String {
      return this.$store.state.createMember.vueModel.stepTwoMemberQuestions.logoFooterDatasource;
    },
    isStepTwo(): Boolean {
      return this.$route.path === this.$store.state.createMember.stepTwoPath;
    },
    isStepThree(): Boolean {
      return this.$route.path === this.$store.state.createMember.stepThreePath;
    },
    formActionRedirectLink(): String {
      if (typeof this.matchedBrandAffinityLink === 'undefined') {
        return '';
      }

      return this.matchedBrandAffinityLink !== null ? this.matchedBrandAffinityLink.url : '';
    }
  },
  mounted() {
    Utility.stickyBasket();
    this.$store.dispatch('createMember/setInitialModel', this.datasourceId);
  },
  methods: {
    close() {
      location.href = '/selvbetjening';
    },
    async dispatchEcommerceEvent(checkoutStep: number) {
      if (!this.productsInBasket.length) {
        await this.$store.dispatch('createMember/setProductsInBasket');
      }
      const proposals = this.productsInBasket;
      let productsArray: Object[] = [];
      for (let i = 0; i < proposals.length; i++) {
        productsArray.push({
          name: proposals[i].Name,
          id: proposals[i].ProductId,
          price: Utility.getGoogleFriendlyPrice(proposals[i].Price),
          brand: Utility.getBrandIdentifier(),
          quantity: 1
        });
      }
      this.$store.dispatch(
        'Events/register',
        {
          event: 'checkout-steps',
          id: 'ecommerce',
          vm: {
            actionField: {
              step: checkoutStep
            },
            products: productsArray
          }
        },
        { root: true }
      );
    },
    dispatchEventMethod(val: number) {
      this.dispatchEcommerceEvent(val);
    },
    goNext() {
      if (this.nextButton.path !== null) {
        this.$router.replace({ path: this.nextButton.path });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all';

#mainView {
  padding-bottom: rem-calc(12);
}
</style>
